/**
 * GameControllerSession
 *
 * - Generiert eine Session-UUID
 * - Zeigt einen QR-Code mit der URL /controller?session=<id>
 * - Öffnet eine WebSocket zum Pairing-Relay
 * - Schließt sich automatisch, sobald das Handy verbunden ist
 * - Solange verbunden: empfängt Input-Events vom Handy und steuert die
 *   Potree-Kamera (links = translate, rechts = yaw/pitch)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { API_URL } from '../../lib/apiBase'

interface Props { onClose: () => void }

interface InputState { lx: number; ly: number; rx: number; ry: number; ex: number; ey: number }

const ZERO: InputState = { lx: 0, ly: 0, rx: 0, ry: 0, ex: 0, ey: 0 }

function makeSessionId(): string {
  if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID()
  return 'sess-' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36)
}

function wsUrlFor(sessionId: string): string {
  const base = API_URL || window.location.origin
  const wsBase = base.replace(/^http/i, 'ws')
  // /ws/yjs/-Prefix, weil der nginx-Proxy aktuell nur dort einen Upgrade
  // durchreicht. Sobald ein generischer /ws/-Block existiert, kann der
  // Pfad wieder auf /ws/controller/ verkürzt werden.
  return `${wsBase}/ws/yjs/controller/${encodeURIComponent(sessionId)}`
}

function controllerPageUrl(sessionId: string): string {
  const origin = window.location.origin
  // Bewusst keine Sub-Route /controller, sondern Query-Param am Root —
  // statische Hosts ohne SPA-Fallback liefern für /controller sonst 404.
  return `${origin}/?ctrl=1&session=${encodeURIComponent(sessionId)}`
}

export function GameControllerSession({ onClose }: Props) {
  const sessionId = useMemo(makeSessionId, [])
  const pageUrl = useMemo(() => controllerPageUrl(sessionId), [sessionId])
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [paired, setPaired] = useState(false)
  const inputRef = useRef<InputState>(ZERO)
  const wsRef = useRef<WebSocket | null>(null)
  const rafRef = useRef<number | null>(null)
  const lastTickRef = useRef<number>(performance.now())

  // QR-Code rendern
  useEffect(() => {
    QRCode.toDataURL(pageUrl, { margin: 1, width: 320 })
      .then(setQrDataUrl)
      .catch((e) => console.warn('[controller] QR generation failed:', e))
  }, [pageUrl])

  // Diagnose-Drossel: nur jedes ~10. Input-Message loggen
  const lastInputLogRef = useRef<number>(0)

  // WS-Verbindung + Pairing
  useEffect(() => {
    const url = wsUrlFor(sessionId)
    console.log('[ctrl viewer] WS →', url)
    const ws = new WebSocket(url)
    wsRef.current = ws
    ws.onopen = () => {
      console.log('[ctrl viewer] WS open, sending hello')
      try { ws.send(JSON.stringify({ type: 'hello', role: 'viewer' })) } catch { /* ignore */ }
    }
    ws.onmessage = (ev) => {
      let msg: any
      try { msg = JSON.parse(String(ev.data)) } catch { return }
      if (!msg) return
      if (msg.type === 'peer-connected' && msg.role === 'controller') {
        console.log('[ctrl viewer] controller paired')
        setPaired(true)
        // AppShell soll seinen aktuellen Section-State publishen,
        // damit der Mobile-Controller sofort die aktive Section sieht.
        window.dispatchEvent(new CustomEvent('orto:controller-state-request'))
      }
      if (msg.type === 'peer-disconnected' && msg.role === 'controller') {
        console.log('[ctrl viewer] controller disconnected')
        inputRef.current = ZERO
      }
      if (msg.type === 'nav') {
        // Mobile→Desktop: Section togglen
        window.dispatchEvent(new CustomEvent('orto:controller-cmd', { detail: msg }))
        return
      }
      if (msg.type === 'input') {
        const next = {
          lx: clampUnit(msg.lx), ly: clampUnit(msg.ly),
          rx: clampUnit(msg.rx), ry: clampUnit(msg.ry),
          ex: clampUnit(msg.ex), ey: clampUnit(msg.ey),
        }
        inputRef.current = next
        const now = performance.now()
        if (now - lastInputLogRef.current > 300) {
          lastInputLogRef.current = now
          console.log('[ctrl viewer] input', next)
        }
      }
    }
    ws.onerror = (e) => { console.warn('[ctrl viewer] WS error', e) }
    ws.onclose = (e) => {
      console.log('[ctrl viewer] WS close', e.code, e.reason)
      wsRef.current = null
    }
    // Desktop→Mobile: aktuelle Section weiterleiten, wenn AppShell sie postet
    const onState = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail) return
      if (ws.readyState !== WebSocket.OPEN) return
      try {
        ws.send(JSON.stringify({ type: 'nav-state', activeSection: detail.activeSection ?? null, ts: Date.now() }))
      } catch { /* ignore */ }
    }
    window.addEventListener('orto:controller-state', onState)

    return () => {
      window.removeEventListener('orto:controller-state', onState)
      try { ws.close(1000, 'unmount') } catch { /* ignore */ }
    }
  }, [sessionId])

  // Kamera-Update-Loop — wartet aktiv auf window.viewer und steuert über
  // lookAt(), weil view.yaw/pitch in der Potree-Version evtl. read-only sind.
  useEffect(() => {
    let cancelled = false
    let internalYaw = 0
    let internalPitch = 0
    let started = false
    let lastDiagLog = 0

    const W = window as any

    function pickVector3(): any {
      return W.Potree?.THREE?.Vector3 || W.THREE?.Vector3 || null
    }

    function tick() {
      if (cancelled) return
      const v = W.viewer
      if (!v?.scene?.view) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      // Initialisierung beim ersten Lauf: yaw/pitch aus dem View übernehmen.
      if (!started) {
        started = true
        internalYaw = typeof v.scene.view.yaw === 'number' ? v.scene.view.yaw : 0
        internalPitch = typeof v.scene.view.pitch === 'number' ? v.scene.view.pitch : 0
        console.log('[ctrl viewer] camera loop initialized', { yaw: internalYaw, pitch: internalPitch, pos: v.scene.view.position?.toArray?.() })
      }

      const now = performance.now()
      const dt = Math.min(0.1, (now - lastTickRef.current) / 1000)
      lastTickRef.current = now

      const view = v.scene.view
      const { lx, ly, rx, ry, ex, ey } = inputRef.current

      // Game-feel-Kurve: bei kleinem Stick-Ausschlag sehr feine Steuerung,
      // bei großem Ausschlag deutlich schneller (sign(v) · v²).
      const curve = (v: number) => Math.sign(v) * v * v

      // Geschwindigkeit skaliert mit der Szenen-Größe (Pivot-Abstand),
      // damit das gleiche Stick-Verhalten in kleinen und großen Pointclouds
      // angenehm bleibt.
      const speedScale = Math.max(1, Math.min(50, view.radius || 10))
      const TRANSLATION_SPEED = 5.0 * speedScale  // m/s bei vollem Ausschlag
      const ROTATION_SPEED = 2.0                   // rad/s bei vollem Ausschlag

      // Blickrichtung als 3D-Vektor (z-up: pitch>0 = nach oben schauen)
      const cosP = Math.cos(internalPitch)
      const sinP = Math.sin(internalPitch)
      const cosY = Math.cos(internalYaw)
      const sinY = Math.sin(internalYaw)
      const fwdX = cosP * cosY, fwdY = cosP * sinY, fwdZ = sinP

      if (lx || ly) {
        const lyC = curve(ly), lxC = curve(lx)
        const d = TRANSLATION_SPEED * dt
        // Vorwärts entlang voller Blickrichtung (inkl. Höhe — wie Minecraft-Kreativmodus)
        view.position.x += fwdX * lyC * d
        view.position.y += fwdY * lyC * d
        view.position.z += fwdZ * lyC * d
        // Strafe horizontal (Rechtsvektor in XY-Ebene, perpendicular zu yaw)
        view.position.x += sinY * lxC * d
        view.position.y += -cosY * lxC * d
      }

      // Zusätzlicher kleiner Stick: ey → vertikal (Welt-Z, blickunabhängig),
      // ex → seitwärts strafen (gleiche Achse wie lx; doppelt vorhanden für
      // präzise Feinbewegung).
      if (ex || ey) {
        const exC = curve(ex), eyC = curve(ey)
        const d = TRANSLATION_SPEED * dt
        view.position.z += eyC * d
        view.position.x += sinY * exC * d
        view.position.y += -cosY * exC * d
      }

      // Rotation: Stick nach oben = nach oben schauen (Vorzeichen rx unverändert,
      // ry jetzt positiv → pitch erhöht → Blick nach oben).
      if (rx) internalYaw -= curve(rx) * ROTATION_SPEED * dt
      if (ry) internalPitch += curve(ry) * ROTATION_SPEED * dt
      const limit = Math.PI / 2 - 0.05
      if (internalPitch > limit) internalPitch = limit
      if (internalPitch < -limit) internalPitch = -limit

      // Kamera-Orientierung anwenden: bevorzugt via lookAt(), Fallback auf
      // direkt gesetzte yaw/pitch.
      const Vec3 = pickVector3()
      if (Vec3) {
        const fx = Math.cos(internalPitch) * Math.cos(internalYaw)
        const fy = Math.cos(internalPitch) * Math.sin(internalYaw)
        const fz = Math.sin(internalPitch)
        const target = new Vec3(
          view.position.x + fx,
          view.position.y + fy,
          view.position.z + fz,
        )
        try { view.lookAt(target) } catch (e) {
          // Manche Potree-Builds erwarten lookAt(x,y,z) — fallback:
          try { view.lookAt(target.x, target.y, target.z) } catch { /* ignore */ }
        }
      } else {
        if (typeof view.yaw === 'number') view.yaw = internalYaw
        if (typeof view.pitch === 'number') view.pitch = internalPitch
      }

      // Diagnose alle ~500ms wenn Bewegung
      if ((lx || ly || rx || ry) && now - lastDiagLog > 500) {
        lastDiagLog = now
        console.log('[ctrl viewer] tick', {
          stick: { lx, ly, rx, ry },
          yaw: internalYaw.toFixed(3),
          pitch: internalPitch.toFixed(3),
          pos: [view.position.x.toFixed(2), view.position.y.toFixed(2), view.position.z.toFixed(2)],
          speedScale: speedScale.toFixed(1),
        })
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { cancelled = true; if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [])

  // ── Rendering ──
  // Vor Pairing: großes QR-Modal. Nach Pairing: kleines Status-Pill am Rand
  // mit Live-Stick-Werten zur visuellen Bestätigung; Komponente bleibt
  // gemountet, damit WS + Camera-Loop weiterleben.
  if (paired) {
    return <ConnectedPill inputRef={inputRef} onClose={onClose} />
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        fontFamily: 'system-ui, sans-serif',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 380, boxShadow: '0 24px 60px rgba(0,0,0,0.35)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Mobil-Controller</h3>
          <button onClick={onClose} style={{ border: 0, background: 'transparent', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px' }}>
          Scanne den QR-Code mit deinem Handy. Sobald die Verbindung steht, wird das Handy zum Controller; dieses Fenster minimiert sich.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 8, background: '#f8fafc', borderRadius: 8 }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR-Code" width={300} height={300} style={{ imageRendering: 'pixelated' }} />
          ) : (
            <div style={{ width: 300, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              QR-Code wird generiert …
            </div>
          )}
        </div>
        <p style={{ fontSize: 11, color: '#64748b', wordBreak: 'break-all', margin: '12px 0 0' }}>
          {pageUrl}
        </p>
      </div>
    </div>
  )
}

function clampUnit(v: any): number {
  const n = Number(v)
  if (!isFinite(n)) return 0
  if (n > 1) return 1
  if (n < -1) return -1
  return n
}

// Live-Anzeige der Stick-Werte; rerendert sich gedrosselt aus dem Ref.
function ConnectedPill({ inputRef, onClose }: { inputRef: React.MutableRefObject<InputState>; onClose: () => void }) {
  const [, force] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => force((n) => (n + 1) % 1_000_000), 80)
    return () => window.clearInterval(id)
  }, [])
  const { lx, ly, rx, ry, ex, ey } = inputRef.current
  const active = lx || ly || rx || ry || ex || ey
  return (
    <div
      style={{
        position: 'fixed', top: 16, right: 16, zIndex: 9998,
        background: 'rgba(15,23,42,0.92)', color: '#e2e8f0',
        borderRadius: 12, padding: '8px 14px',
        display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: 'system-ui, sans-serif', fontSize: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? '#facc15' : '#22c55e' }} />
      <span>Controller</span>
      <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#94a3b8' }}>
        L {lx.toFixed(2)},{ly.toFixed(2)}  R {rx.toFixed(2)},{ry.toFixed(2)}  E {ex.toFixed(2)},{ey.toFixed(2)}
      </code>
      <button
        onClick={onClose}
        style={{ border: 0, background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
        title="Trennen"
      >×</button>
    </div>
  )
}
